import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, RefreshCwIcon, InfoIcon, ShieldCheckIcon, ExternalLinkIcon, CopyIcon, CheckIcon, GlobeIcon, UsersIcon, KeyIcon, MailIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { emailNotificationService, EmailConfig } from '../../utils/emailNotifications';
export function VerificationCodesConfig() {
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: true,
    senderName: 'Savidna'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  // Template variables for verification codes
  const verificationVars = [{
    name: 'to_email',
    desc: 'Correo del usuario'
  }, {
    name: 'to_name',
    desc: 'Nombre del usuario'
  }, {
    name: 'from_name',
    desc: 'Nombre del remitente'
  }, {
    name: 'subject',
    desc: 'Asunto del correo'
  }, {
    name: 'message',
    desc: 'Código de verificación y mensaje'
  }];
  useEffect(() => {
    const loadConfig = async () => {
      const savedConfig = emailNotificationService.getConfig();
      if (savedConfig) {
        setConfig({
          ...savedConfig,
          enabled: true
        });
      }
      await emailNotificationService.loadFromSupabase();
      const freshConfig = emailNotificationService.getConfig();
      if (freshConfig) {
        setConfig({
          ...freshConfig,
          enabled: true
        });
      }
    };
    loadConfig();
  }, []);
  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    const result = await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (result.success && !result.error) {
      setSaveMessage('✅ Configuración guardada. Los códigos de verificación se enviarán por correo.');
    } else if (result.success && result.error) {
      setSaveMessage(`⚠️ ${result.error}`);
    } else {
      setSaveMessage('❌ Error al guardar la configuración');
    }
    setIsSaving(false);
    setTimeout(() => setSaveMessage(''), 5000);
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (!config.publicKey || !config.serviceId || !config.templateId) {
      setTestResult({
        success: false,
        message: 'Completa todos los campos: Public Key, Service ID y Template ID.'
      });
      setIsTesting(false);
      return;
    }
    setTestResult({
      success: true,
      message: '✅ Configuración válida. Los códigos de verificación se enviarán automáticamente por correo cuando un paciente se registre.'
    });
    setIsTesting(false);
  };
  const handleCopyVar = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 1500);
  };
  const isConfigComplete = config.publicKey && config.serviceId && config.templateId;
  return <div className="space-y-6" data-id="element-4283">
      <div data-id="element-4284">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4285">
          <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-4286" />
          Códigos de Verificación por Email
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4287">
          Envía códigos de verificación de 6 dígitos a los pacientes cuando se
          registran
        </p>
      </div>

      {/* Status Card */}
      <Card className={`${isConfigComplete ? 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800'}`} data-id="element-4288">
        <div className="flex items-center justify-between" data-id="element-4289">
          <div className="flex items-center gap-4" data-id="element-4290">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${isConfigComplete ? 'bg-purple-500 dark:bg-purple-600 shadow-purple-500/20' : 'bg-amber-500 dark:bg-amber-600 shadow-amber-500/20'}`} data-id="element-4291">
              <KeyIcon className="w-7 h-7 text-white" data-id="element-4292" />
            </div>
            <div data-id="element-4293">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4294">
                {isConfigComplete ? '✅ Verificación Activa' : '⚠️ Configuración Pendiente'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4295">
                {isConfigComplete ? 'Los códigos se envían automáticamente al correo del paciente' : 'Configura EmailJS para enviar códigos de verificación reales'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 rounded-full border border-gray-200 dark:border-gray-700" data-id="element-4296">
            <UsersIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-4297" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400" data-id="element-4298">
              Compartido
            </span>
          </div>
        </div>
      </Card>

      {/* How it works */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm" data-id="element-4299">
        <div className="flex items-start gap-3" data-id="element-4300">
          <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-4301" />
          <div data-id="element-4302">
            <p className="font-medium mb-1" data-id="element-4303">¿Cómo funciona?</p>
            <ul className="text-blue-600 dark:text-blue-400 space-y-1 text-xs" data-id="element-4304">
              <li data-id="element-4305">
                • Cuando un paciente se registra, se genera un código de 6
                dígitos
              </li>
              <li data-id="element-4306">
                • El código se envía automáticamente a su correo electrónico
              </li>
              <li data-id="element-4307">• El paciente ingresa el código para verificar su email</li>
              <li data-id="element-4308">• Los códigos expiran en 10 minutos por seguridad</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Same config notice */}
      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-purple-700 dark:text-purple-300 text-sm flex items-start gap-2" data-id="element-4309">
        <GlobeIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-4310" />
        <div data-id="element-4311">
          <p className="font-medium" data-id="element-4312">Usa la misma configuración de EmailJS</p>
          <p className="text-purple-600 dark:text-purple-400 mt-0.5" data-id="element-4313">
            Los códigos de verificación usan la misma configuración de EmailJS
            que las notificaciones de citas. Si ya configuraste EmailJS, ¡ya
            está listo!
          </p>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg border text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : saveMessage.startsWith('⚠️') ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-4314">
          {saveMessage}
        </div>}

      {/* EmailJS Configuration */}
      <Card data-id="element-4315">
        <div className="space-y-4" data-id="element-4316">
          <div className="flex items-center justify-between" data-id="element-4317">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-4318">
              Credenciales de EmailJS
            </h3>
            <a href="https://dashboard.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1" data-id="element-4319">
              Abrir Dashboard
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-4320" />
            </a>
          </div>

          <Input label="Public Key" value={config.publicKey} onChange={e => setConfig({
          ...config,
          publicKey: e.target.value
        })} placeholder="Ej: aBcDeFgHiJkLmN" helperText="Dashboard → Account → Public Key" data-id="element-4321" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4322">
            <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
            ...config,
            serviceId: e.target.value
          })} placeholder="Ej: service_abc123" helperText="Dashboard → Email Services → Service ID" data-id="element-4323" />
            <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
            ...config,
            templateId: e.target.value
          })} placeholder="Ej: template_xyz789" helperText="Dashboard → Email Templates → Template ID" data-id="element-4324" />
          </div>

          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: e.target.value
        })} placeholder="Savidna - Centro de Bienestar" data-id="element-4325" />

          <div className="flex items-center gap-3 pt-4 flex-wrap" data-id="element-4326">
            <Button onClick={handleSave} isLoading={isSaving} data-id="element-4327">
              Guardar
            </Button>
            <Button variant="secondary" onClick={handleTestConnection} isLoading={isTesting} disabled={!isConfigComplete} data-id="element-4328">
              <RefreshCwIcon className="w-4 h-4" data-id="element-4329" />
              Verificar
            </Button>
            <Button variant="secondary" onClick={() => setShowInstructions(!showInstructions)} data-id="element-4330">
              <InfoIcon className="w-4 h-4" data-id="element-4331" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && <div className={`p-3 rounded-lg border flex items-start gap-2 ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'}`} data-id="element-4332">
              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-4333" /> : <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-4334" />}
              <p className="font-medium whitespace-pre-line" data-id="element-4335">
                {testResult.message}
              </p>
            </div>}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && <Card data-id="element-4336">
          <div className="space-y-6" data-id="element-4337">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-4338">
              Configuración Rápida
            </h3>

            <div className="space-y-6" data-id="element-4339">
              {/* Step 1 */}
              <div className="flex gap-4" data-id="element-4340">
                <div className="flex-shrink-0" data-id="element-4341">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-4342">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-4343">
                      1
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-4344">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4345">
                    Crea una cuenta en EmailJS
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4346">
                    Ve a{' '}
                    <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline" data-id="element-4347">
                      emailjs.com
                    </a>{' '}
                    y regístrate gratis (200 correos/mes).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4" data-id="element-4348">
                <div className="flex-shrink-0" data-id="element-4349">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-4350">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-4351">
                      2
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-4352">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4353">
                    Conecta tu Gmail
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4354">
                    Dashboard → Email Services → Add New Service → Gmail. Copia
                    el <strong data-id="element-4355">Service ID</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4" data-id="element-4356">
                <div className="flex-shrink-0" data-id="element-4357">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-4358">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-4359">
                      3
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-4360">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4361">
                    Crea o usa tu plantilla existente
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4362">
                    Si ya tienes una plantilla para notificaciones de citas,{' '}
                    <strong data-id="element-4363">¡úsala!</strong> Los códigos de verificación usan la
                    misma plantilla. Solo asegúrate de incluir la variable{' '}
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-4364">
                      {'{{message}}'}
                    </code>
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-4365">
                    <div className="grid grid-cols-1 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4366">
                      {verificationVars.map(v => <div key={v.name} className="flex items-center justify-between px-3 py-2" data-id="element-4367">
                          <div className="flex items-center gap-2" data-id="element-4368">
                            <code className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono" data-id="element-4369">
                              {`{{${v.name}}}`}
                            </code>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4370">
                              {v.desc}
                            </span>
                          </div>
                          <button onClick={() => handleCopyVar(v.name)} className="p-1 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded transition-colors" title="Copiar" data-id="element-4371">
                            {copiedVar === v.name ? <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-4372" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-4373" />}
                          </button>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4" data-id="element-4374">
                <div className="flex-shrink-0" data-id="element-4375">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-4376">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-4377">
                      4
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-4378">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4379">
                    Pega las credenciales aquí
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4380">
                    Copia el Public Key, Service ID y Template ID de EmailJS y
                    pégalos arriba. Haz clic en Guardar y ¡listo!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4" data-id="element-4381">
              <p className="text-sm text-purple-700 dark:text-purple-300" data-id="element-4382">
                <strong data-id="element-4383">✅ ¡Listo!</strong> Los códigos de verificación se
                enviarán automáticamente al correo del paciente cuando se
                registre. El código expira en 10 minutos.
              </p>
            </div>
          </div>
        </Card>}

      {/* Example email preview */}
      <Card data-id="element-4384">
        <div className="space-y-3" data-id="element-4385">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4386">
            <MailIcon className="w-5 h-5 text-purple-600" data-id="element-4387" />
            Vista Previa del Correo
          </h3>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" data-id="element-4388">
            <div className="space-y-2 text-sm" data-id="element-4389">
              <p className="font-semibold text-gray-900 dark:text-white" data-id="element-4390">
                Asunto: 🔐 Código de Verificación - {config.senderName}
              </p>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 text-gray-600 dark:text-gray-400" data-id="element-4391">
                <p data-id="element-4392">Hola,</p>
                <p className="mt-2" data-id="element-4393">Tu código de verificación es:</p>
                <p className="my-3 text-2xl font-bold text-purple-600 dark:text-purple-400" data-id="element-4394">
                  123456
                </p>
                <p className="text-xs text-gray-500" data-id="element-4395">
                  Este código expira en 10 minutos.
                </p>
                <p className="mt-3 text-xs text-gray-500" data-id="element-4396">
                  Si no solicitaste este código, ignora este correo.
                </p>
                <p className="mt-4 text-xs" data-id="element-4397">
                  ---
                  <br data-id="element-4398" />
                  Saludos,
                  <br data-id="element-4399" />
                  {config.senderName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}